// HomePage.js
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { styled } from "@mui/material/styles";

// Local components
import AppShell from "./AppShell";
import SearchSideContent from "./SearchSideContent";
import SearchBodyHeader from "./SearchBodyHeader";
import SearchBodyContent from "./SearchBodyContent";
import ScrollToTopButton from "./ScrollToTopButton";
import FirearmDetailPane from "./FirearmDetailPane";
import MuiPaginationView, { setBeforePageChange } from "./facets/MuiPaginationView";
import DataBar from "./DataBar";

const StickyPaginationFooter = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// SearchUI components
import { setFirearmSlug, subscribeFirearm } from "../searchui/firearmUrlSync";
import { buildFirearmSlug, extractFrn } from "../utils/firearmLink";
import {
  ErrorBoundary,
  Paging,
  WithSearch,
} from "@elastic/react-search-ui";

export default function HomePage() {
  return (
    <WithSearch
      mapContextToProps={({
        wasSearched,
        results,
        totalResults,
        resultsPerPage,
        isLoading,
        addFilter,
      }) => ({
        wasSearched,
        results,
        totalResults,
        resultsPerPage,
        isLoading,
        addFilter,
      })}
    >
      {({
        wasSearched,
        results,
        totalResults,
        resultsPerPage,
        isLoading,
        addFilter,
      }) => {

        var totalPagesCap = (Math.ceil(totalResults / resultsPerPage) < 100) ? Math.ceil(totalResults / resultsPerPage) : 100;
        var initial_loader = (isLoading && !wasSearched) ? true : false;

        const location = useLocation();
        const [selectedResult, setSelectedResult] = useState(null);
        const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
        const autoSelectedRef = useRef(false);
        const stateAppliedRef = useRef(false);
        const targetFrnRef = useRef(null);

        // Pagination click: hide the cards via direct DOM (synchronous,
        // bypasses React batching), then reset body scrollTop. By the
        // time the browser repaints, the cards are display:none and
        // scroll is at 0 — there's nothing to "slide" up.
        useEffect(() => {
          setBeforePageChange(() => {
            const cardsEl = document.getElementById("body-cards");
            if (cardsEl) cardsEl.style.display = "none";
            const bodyEl = document.getElementById("body-pane");
            if (bodyEl) bodyEl.scrollTop = 0;
          });
          return () => setBeforePageChange(null);
        }, []);
        useLayoutEffect(() => {
          if (!isLoading) {
            const cardsEl = document.getElementById("body-cards");
            if (cardsEl) cardsEl.style.display = "";
          }
        }, [isLoading, results]);

        // Capture target FRN from ?firearm=<slug> for auto-select.
        useEffect(() => {
          const params = new URLSearchParams(window.location.search);
          const slug = params.get("firearm");
          targetFrnRef.current = slug
            ? extractFrn(slug)
            : location.state?.selectFrn || null;
        }, []);

        // Sync URL-encoded filters into Elastic state on mount. Elastic's
        // outer (lifted) SearchProvider doesn't re-read the URL on React
        // Router navigations, so when the breadcrumb pushes a URL with a
        // filter we have to apply it via the action API. Only handles
        // simple field/values[0]/type encoding (what FirearmPage emits).
        useEffect(() => {
          if (stateAppliedRef.current) return;
          stateAppliedRef.current = true;
          const params = new URLSearchParams(window.location.search);
          const field = params.get("filters[0][field]");
          const value = params.get("filters[0][values][0]");
          const type = params.get("filters[0][type]") || "any";
          if (field && value) {
            addFilter(field, value, type);
          } else if (location.state?.filterMake) {
            addFilter("make", location.state.filterMake, "any");
          }
        }, [location.state, addFilter]);

        // Auto-select the result whose FRN matches the captured target.
        useEffect(() => {
          if (autoSelectedRef.current) return;
          const targetFrn = targetFrnRef.current;
          if (!targetFrn || results.length === 0) return;
          const match = results.find((r) => r.frn?.raw === targetFrn);
          if (match) {
            setSelectedResult(match);
            autoSelectedRef.current = true;
          }
        }, [results]);

        // Reflect selection in URL as ?firearm=<slug>.
        useEffect(() => {
          if (!selectedResult && targetFrnRef.current && !autoSelectedRef.current) {
            return;
          }
          const slug = selectedResult
            ? buildFirearmSlug({
                frn: selectedResult.frn?.raw,
                make: selectedResult.make?.raw,
                model: selectedResult.model?.raw,
              })
            : null;
          setFirearmSlug(slug);
        }, [selectedResult]);

        // React to back/forward URL changes AND header-driven clears.
        useEffect(() => {
          return subscribeFirearm((slug) => {
            if (!slug) {
              setSelectedResult(null);
              return;
            }
            const frn = extractFrn(slug);
            if (frn && frn !== selectedResult?.frn?.raw) {
              targetFrnRef.current = frn;
              autoSelectedRef.current = false;
              setSelectedResult(null);
            }
          });
        }, [selectedResult]);

        const selectedFrn = selectedResult?.frn?.raw ?? null;

        return (
          <div className="App">
            <ErrorBoundary>
              <AppShell
                dataBar={<DataBar wasSearched={wasSearched} initialLoader={initial_loader} />}
                filters={<SearchSideContent />}
                bodyHeader={
                  <SearchBodyHeader
                    onOpenFilters={() => setFiltersDrawerOpen(true)}
                  />
                }
                body={
                  <SearchBodyContent
                    initialLoader={initial_loader}
                    results={results}
                    selectedFrn={selectedFrn}
                    onSelect={(r) => setSelectedResult(r)}
                  />
                }
                bodyFooter={
                  <StickyPaginationFooter>
                    <Paging totalPages={totalPagesCap} view={MuiPaginationView} />
                  </StickyPaginationFooter>
                }
                detail={
                  <FirearmDetailPane
                    result={selectedResult}
                    onClose={() => setSelectedResult(null)}
                  />
                }
                filtersDrawerOpen={filtersDrawerOpen}
                onFiltersClose={() => setFiltersDrawerOpen(false)}
                detailOpen={!!selectedResult}
                onDetailClose={() => setSelectedResult(null)}
              />
            </ErrorBoundary>
            {(!initial_loader && wasSearched) && <ScrollToTopButton />}
            <Helmet>
              <title>Canadian Firearms Data & Analytics | Armalytics</title>
            </Helmet>
          </div>
        );
      }}
    </WithSearch>
  );
}
